import axiosNest from '../../api/axiosNest';
import { toErrorMessage } from '../userOffers/orderActionsApi';

/**
 * Raporty dla klientów (CS) — klient API (`apps/api/src/client-reports/`;
 * prace 26.10.2026, pakiet P2; przełączniki `clientReportConfig`,
 * S-26.10-04, i `clientReportQueue`, S-26.10-05). **Z systemu nic nie jest
 * wysyłane** — plik pobiera przeglądarka, wiadomość wysyła CS ze swojej
 * poczty i oznacza „wysłane ręcznie”.
 */
export const FEATURE_CLIENT_REPORT_CONFIG = 'clientReportConfig';
export const FEATURE_CLIENT_REPORT_QUEUE = 'clientReportQueue';

export { toErrorMessage };

/** Kolumny raportu — klucze z API (dane), etykiety jak w API. */
export const REPORT_COLUMNS: ReadonlyArray<{ id: string; name: string; onRequest?: boolean }> = [
  { id: 'orderNumber', name: 'Numer zlecenia' },
  { id: 'clientReference', name: 'Referencja klienta' },
  { id: 'vehicle', name: 'Pojazd' },
  { id: 'trailer', name: 'Naczepa' },
  { id: 'driver', name: 'Kierowca', onRequest: true },
  { id: 'trimbleTerminal', name: 'Nr terminala Trimble', onRequest: true },
  { id: 'loading', name: 'Załadunek (plan / potwierdzone)' },
  { id: 'unloading', name: 'Rozładunek (plan / potwierdzone)' },
  { id: 'status', name: 'Status' },
];

/** Zaznaczone w formularzu nowej konfiguracji (układ makiety) — bez danych kierowcy. */
export const SUGGESTED_COLUMNS = REPORT_COLUMNS.filter((c) => !c.onRequest).map((c) => c.id);

export const FREQUENCIES = [
  { id: 'daily', name: 'codziennie' },
  { id: 'workdays', name: 'w dni robocze (pn–pt)' },
  { id: 'weekly', name: 'co tydzień' },
];

export const WEEKDAYS = [
  { id: 1, name: 'poniedziałek' },
  { id: 2, name: 'wtorek' },
  { id: 3, name: 'środa' },
  { id: 4, name: 'czwartek' },
  { id: 5, name: 'piątek' },
  { id: 6, name: 'sobota' },
  { id: 7, name: 'niedziela' },
];

export const DATE_BASES = [
  { id: 'loading', name: 'po dacie załadunku' },
  { id: 'unloading', name: 'po dacie rozładunku' },
];

export const FILE_FORMATS = [
  { id: 'xlsx', name: 'Excel (XLSX)' },
  { id: 'csv', name: 'CSV' },
];

export interface ReportLocation {
  country: string;
  city: string | null;
}

export interface ReportConfig {
  id: number;
  contractorId: number;
  clientName: string;
  clientTmsId: number | null;
  name: string;
  dateBasis: string;
  dateBasisLabel: string;
  rangeStartDays: number;
  rangeEndDays: number;
  rangeLabel: string;
  locations: ReportLocation[];
  locationsLabel: string;
  columns: string[];
  columnsLabel: string;
  frequency: string;
  weekday: number | null;
  sendTime: string;
  scheduleLabel: string;
  recipients: string | null;
  fileFormat: string;
  fileFormatLabel: string;
  autoSend: false;
  owner: { id: number; name: string | null } | null;
  validFrom: string;
  disabled: boolean;
  disabledByName: string | null;
  disabledReason: string | null;
  createdByName: string | null;
  nextDueAt: string | null;
}

export interface ConfigChange {
  id: number;
  kind: string;
  kindLabel: string;
  reason: string;
  changes: Array<{ field: string; label: string; before: string | null; after: string | null }>;
  changedByName: string | null;
  changedAt: string;
}

export interface ConfigInput {
  contractorId?: number;
  name: string;
  dateBasis: string;
  rangeStartDays: number;
  rangeEndDays: number;
  locations: ReportLocation[];
  columns: string[];
  frequency: string;
  weekday: number | null;
  sendTime: string;
  recipients: string;
  fileFormat: string;
  ownerId: number | null;
  validFrom: string;
  reason: string;
}

export async function fetchConfigs(includeDisabled: boolean): Promise<ReportConfig[]> {
  const { data } = await axiosNest.get<ReportConfig[]>('/client-report-configs', {
    params: includeDisabled ? { includeDisabled: '1' } : {},
  });
  return data ?? [];
}

export async function fetchOwners(): Promise<Array<{ id: number; username: string }>> {
  const { data } = await axiosNest.get<Array<{ id: number; username: string }>>('/client-report-configs/owners');
  return data ?? [];
}

export async function fetchConfigHistory(id: number): Promise<ConfigChange[]> {
  const { data } = await axiosNest.get<ConfigChange[]>(`/client-report-configs/${id}/history`);
  return data ?? [];
}

export async function createConfig(input: ConfigInput): Promise<ReportConfig> {
  const { data } = await axiosNest.post<ReportConfig>('/client-report-configs', input);
  return data;
}

export async function updateConfig(id: number, input: Partial<ConfigInput>): Promise<ReportConfig> {
  const { data } = await axiosNest.put<ReportConfig>(`/client-report-configs/${id}`, input);
  return data;
}

export async function setConfigDisabled(id: number, disabled: boolean, reason: string): Promise<ReportConfig> {
  const { data } = await axiosNest.post<ReportConfig>(
    `/client-report-configs/${id}/${disabled ? 'disable' : 'enable'}`,
    { reason },
  );
  return data;
}

export async function prepareNow(configId: number): Promise<ReportDetail> {
  const { data } = await axiosNest.post<ReportDetail>(`/client-report-configs/${configId}/prepare-now`, {});
  return data;
}

/** Kontrahent do wyboru — wyszukiwanie po nazwie w kartotece. */
export async function searchContractors(q: string): Promise<Array<{ id: number; legalName: string }>> {
  const { data } = await axiosNest.get<Array<{ id: number; legalName: string }>>('/contractors', {
    params: { filter: JSON.stringify({ q }), range: JSON.stringify([0, 19]), sort: JSON.stringify(['legalName', 'ASC']) },
  });
  return data ?? [];
}

// ── Kolejka ──────────────────────────────────────────────────────────

export type CellOrigin = 'confirmed' | 'plan' | 'registry' | 'missing' | 'info';

export interface ReportCell {
  value: string | null;
  origin: CellOrigin;
  source?: string;
}

export interface ReportRow {
  userOfferId: number;
  cells: Record<string, ReportCell | undefined>;
  missing: string[];
}

export interface ReportSummary {
  id: number;
  configId: number;
  contractorId: number;
  reportName: string;
  clientName: string;
  locationsLabel: string;
  trigger: string;
  triggerLabel: string;
  dueAt: string;
  periodFrom: string;
  periodTo: string;
  periodLabel: string;
  status: string;
  statusLabel: string;
  overdue: boolean;
  rowCount: number | null;
  missingCount: number | null;
  preparedAt: string | null;
  preparedByName: string | null;
  sentAt: string | null;
  sentByName: string | null;
  heldAt: string | null;
  heldByName: string | null;
  holdReason: string | null;
}

export interface ReportDetail extends ReportSummary {
  dateBasisLabel: string;
  columns: string[];
  headers: string[];
  rows: ReportRow[];
  truncated: boolean;
  recipients: string[];
  fileFormat: string;
  fileFormatLabel: string;
  sentRecipients: string | null;
  sentWithMissing: boolean | null;
  draft: { subject: string; body: string; template: string } | null;
  events: Array<{ id: number; kind: string; kindLabel: string; note: string | null; byName: string | null; at: string }>;
  planNote: string;
}

export interface ReportQueue {
  items: ReportSummary[];
  statuses: Array<{ id: string; name: string }>;
  counts: { open: number; incomplete: number; toAccept: number; overdue: number };
}

export interface QueueFilter {
  status?: string;
  overdue?: boolean;
  client?: string;
}

export async function fetchQueue(f: QueueFilter): Promise<ReportQueue> {
  const params: Record<string, string> = {};
  if (f.status) params.status = f.status;
  if (f.overdue) params.overdue = '1';
  if (f.client?.trim()) params.client = f.client.trim();
  const { data } = await axiosNest.get<ReportQueue>('/client-reports', { params });
  return data;
}

export async function fetchReport(id: number): Promise<ReportDetail> {
  const { data } = await axiosNest.get<ReportDetail>(`/client-reports/${id}`);
  return data;
}

export async function prepareReport(id: number): Promise<ReportDetail> {
  const { data } = await axiosNest.post<ReportDetail>(`/client-reports/${id}/prepare`, {});
  return data;
}

export async function markSent(
  id: number,
  body: { recipients?: string; withMissing?: boolean; note?: string },
): Promise<ReportDetail> {
  const { data } = await axiosNest.post<ReportDetail>(`/client-reports/${id}/sent`, body);
  return data;
}

export async function holdReport(id: number, reason: string): Promise<ReportDetail> {
  const { data } = await axiosNest.post<ReportDetail>(`/client-reports/${id}/hold`, { reason });
  return data;
}

export async function resumeReport(id: number): Promise<ReportDetail> {
  const { data } = await axiosNest.post<ReportDetail>(`/client-reports/${id}/resume`, {});
  return data;
}

/** Nazwa pliku z nagłówka odpowiedzi; bez niego — z numeru raportu. */
export function fileNameFrom(disposition: string | undefined, id: number, format: string): string {
  const m = /filename="([^"]+)"/.exec(disposition ?? '');
  return m ? m[1] : `raport-${id}.${format}`;
}

/** Plik raportu z API — zapis na dysk przez przeglądarkę. */
export async function downloadReportFile(id: number, format: string): Promise<void> {
  const res = await axiosNest.get<Blob>(`/client-reports/${id}/file`, { params: { format }, responseType: 'blob' });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = url;
  a.download = fileNameFrom(res.headers?.['content-disposition'] as string | undefined, id, format);
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Najdłuższy odnośnik `mailto:` — jak przy zapytaniach do przewoźników. */
export const MAILTO_MAX_LENGTH = 2000;
export const MAILTO_PASTE_NOTE = '[Wklej tutaj treść wiadomości — jest w schowku (Ctrl+V)]';

/**
 * Odnośnik `mailto:` ze szkicem raportu: adresaci z konfiguracji (po
 * przecinku), temat i treść. Za długa treść nie trafia do odnośnika — ekran
 * kopiuje ją do schowka. Załącznik (plik raportu) CS dołącza sam.
 */
export function reportMailto(
  recipients: readonly string[],
  subject: string,
  body: string,
): { href: string; bodyIncluded: boolean } {
  const to = recipients.map((r) => encodeURIComponent(r)).join(',');
  const build = (b: string | null) =>
    `mailto:${to}?subject=${encodeURIComponent(subject)}${b === null ? '' : `&body=${encodeURIComponent(b)}`}`;
  const full = build(body);
  if (full.length <= MAILTO_MAX_LENGTH) return { href: full, bodyIncluded: true };
  const note = build(MAILTO_PASTE_NOTE);
  return { href: note.length <= MAILTO_MAX_LENGTH ? note : build(null), bodyIncluded: false };
}

export const STATUS_COLOR: Record<string, 'default' | 'warning' | 'error' | 'success' | 'info'> = {
  to_prepare: 'default',
  incomplete: 'error',
  to_accept: 'warning',
  sent: 'success',
  on_hold: 'info',
};
